import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import { useOneTimeOrder } from '../../OneTimeOrderProvider';
import {
  ONE_TIME_ORDER_STEPS,
} from '../../../../utils/oneTimeOrder';
import {
  SUBSCRIPTION_PAYMENT_TYPES,
  setSubscriptionOrderItemsInStorage,
} from '../../../../utils/subscriptions';
import NavigationBar from '../../../../global/NavigationBar';
import MembershipOrderTbbContainer from '../../../MembershipOrderTbb/MembershipOrderTbbContainer';
import { MEMBERSHIP_ORDER_TBB_STEPS, MEMBERSHIP_ORDER_TBB_TYPE } from '../../../MembershipOrderTbb/MembershipOrderTbbContext';
import { getFilteredMembershipItems, getSelectionTotalDisplay } from '../../../../utils/membership';
import { getTrackerIdEventName, SUBSCRIPTION_TRACKER_TYPES, TRACKER_IDS } from '../../../../config/tracker';
import useScrollToTop from '../../../../utils/hooks/useScrollToTop';
import { RECYCLING_PRODUCT_TYPES } from '../../../../utils/tbbRegistration';
import { Button, BUTTON_TYPE, BUTTON_SIZE } from '../../../../global/Button';
import { isMobile, SIZE_XS, SIZE_SM } from '../../../../../../hooks/useBreakPoint';
import Drawer from '../../../../global/Mui/Drawer';
import Dialog from '../../../../global/Mui/Dialog';

import './OneTimeOrderSelection.scss';

const {
  oneTimeOrder: {
    selection: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
      [SUBSCRIPTION_TRACKER_TYPES.CLOSE]: trackerClose,
      [SUBSCRIPTION_TRACKER_TYPES.SUBMIT]: trackerSubmit,
      [SUBSCRIPTION_TRACKER_TYPES.CANCEL]: trackerCancel,
    },
  },
} = TRACKER_IDS;

const OneTimeOrderSelection = () => {
  useScrollToTop();
  const history = useHistory();
  const { isSignedIn } = useFirebaseAuth();
  const isMobileSize = isMobile([SIZE_XS, SIZE_SM]);

  const {
    setStep,
    address,
    setAddress,
    subtotal,
    subscriptionPlan,
    orderItems,
    setOrderItems,
    setShippingPrice,
    posthog,
  } = useOneTimeOrder();

  const [isAddressValid, setIsAddressValid] = useState(false);
  const [productsStep, setProductsStep] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const setFilteredProducts = useCallback((updatedProducts) => {
    setSubscriptionOrderItemsInStorage(updatedProducts);

    const filteredProducts = { ...updatedProducts };
    delete filteredProducts[
      RECYCLING_PRODUCT_TYPES.VIRTUAL
    ];
    setOrderItems(filteredProducts);
  }, []);

  const handleOnBack = useCallback(() => {
    posthog.capture(getTrackerIdEventName(trackerClose));
    history.goBack();
  }, []);

  const handleOnAddressValidated = useCallback((isValid, shippingPrice) => {
    setIsAddressValid(isValid);
    setShippingPrice(shippingPrice);
  }, []);

  const submitDisabled = useMemo(() => {
    if (!isAddressValid || productsStep !== MEMBERSHIP_ORDER_TBB_STEPS.PRODUCT_SELECTION) {
      return true;
    }

    const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME)
      .map(item => item.id);

    const mappedProducts = Object.entries(orderItems)
      .filter(([productType, quantity]) => (
        membershipItems.includes(productType) && quantity > 0
      ));

    return mappedProducts.length === 0;
  }, [isAddressValid, productsStep, orderItems]);

  const getCTAContent = useCallback((confirmed) => {
    const savings = Math.max(subtotal - (subscriptionPlan?.value ?? 0), 0);

    const contentData = confirmed ? {
      title: `Save ${savings > 0 ? `$${savings}` : ''} today!`,
      description: 'Get unlimited clean outs for the full year with Trashie Unlimited.',
      submitText: 'Go Unlimited',
      onSubmit: () => {
        posthog.capture(getTrackerIdEventName(trackerSubmit));
        history.goBack();
      },
      cancelText: 'Continue with one-time purchase',
      onCancel: () => {
        posthog.capture(getTrackerIdEventName(trackerCancel));
        setStep(ONE_TIME_ORDER_STEPS.PAYMENT);
      },
      onDiscard: () => setShowConfirmation(false),
    } : {
      description: (
        <>
          <span>{getSelectionTotalDisplay(orderItems, address, orderItems)}</span>
          <span>
            <b>{`$${subtotal} `}</b>
            (Before shipping & tax)
          </span>
        </>
      ),
      submitText: 'Continue to Payment',
      onSubmit: () => setShowConfirmation(true),
    };

    return (
      <div className="OneTimeOrderSelection__content">
        {contentData.title && (
          <div className="OneTimeOrderSelection__content-title">
            {contentData.title}
          </div>
        )}
        {contentData.description && (
          <div className="OneTimeOrderSelection__content-description">
            {contentData.description}
          </div>
        )}
        {contentData.onSubmit && (
          <Button
            type={BUTTON_TYPE.QUATERNARY}
            size={BUTTON_SIZE.LARGE}
            onClick={contentData.onSubmit}
            disabled={submitDisabled}
          >
            {contentData.submitText || 'Continue'}
          </Button>
        )}
        {contentData.onCancel && (
          <Button
            type={BUTTON_TYPE.LINK_SEPTENARY}
            size={BUTTON_SIZE.LARGE}
            onClick={contentData.onCancel}
          >
            {contentData.cancelText || 'Cancel'}
          </Button>
        )}
      </div>
    );
  }, [subtotal, subscriptionPlan?.value, orderItems, address, submitDisabled]);

  const getConfirmation = useCallback(() => {
    const confirmationContent = getCTAContent(true);

    return isMobileSize ? (
      <Drawer
        open
        anchor="bottom"
        onClose={() => setShowConfirmation(false)}
      >
        {confirmationContent}
      </Drawer>
    ) : (
      <Dialog
        open
        onClose={() => setShowConfirmation(false)}
      >
        {confirmationContent}
      </Dialog>
    );
  }, [isMobileSize, getCTAContent]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, []);

  return (
    <div className="OneTimeOrderSelection">
      <NavigationBar
        className="OneTimeOrderSelection__navigationBar"
        onGoBack={handleOnBack}
      />
      <MembershipOrderTbbContainer
        containerClassName="OneTimeOrderSelection__section"
        defaultValues={orderItems}
        useWhiteCards
        hideOrderCta
        onProductsChange={setFilteredProducts}
        onAddressChange={setAddress}
        onAddressValidated={handleOnAddressValidated}
        onStepChange={setProductsStep}
        orderType={MEMBERSHIP_ORDER_TBB_TYPE.ONE_TIME}
        isAnonymous={!isSignedIn}
      />
      <div className="OneTimeOrderSelection__cta">
        {getCTAContent()}
      </div>
      {showConfirmation && getConfirmation()}
    </div>
  );
};

export default OneTimeOrderSelection;
